<template>
    <div :class="['block', 'block-featured-slides', 'carousel-item']" data-aos="custom-fade-in">
        <div v-if="json" :id="swiperContainerId" class="swiper-container">
            <div :class="['swiper-wrapper', (json.length > 1) ? 'carousel' : 'individual']">
                <div
                    :class="['swiper-slide', (json.length === 1) ? 'swiper-slide-active' : '']"
                    v-for="(item, index) in json"
                    :key="`featured-${index}-${Math.random()}`"
                >
                    <div class="swipe-overlay"></div>

                    <!-- Vimeo (unchanged) -->
                    <div v-if="item.vimeoId" :data-vimeoId="item.vimeoId" :class="['bgimg', 'iframe-holder']"></div>

                    <!-- MP4 with lazy-video -->
                    <div v-else-if="item.video?.length > 0" class="bgimg" data-aos="custom-fade-in">
                        <video
                            preload="metadata"
                            autoplay
                            muted
                            loop
                            playsinline
                            webkit-playsinline
                            :poster="item.image?.[0]?.url"
                            v-lazy-video
                        >
                            <source :data-src="item.video?.[0]?.url" type="video/mp4" />
                        </video>
                        <div v-if="rollover" class="rollover">
                            <inline-svg class="plus-rollover" src="/assets/site/images/plus-rollover.svg" />
                        </div>
                    </div>

                    <!-- Image -->
                    <div
                        v-else-if="index < 5"
                        data-aos="custom-fade-in"
                        class="bgimg"
                        :style="item.image?.[0]?.bgStyle"
                    >
                        <div v-if="rollover" class="rollover">
                            <inline-svg class="plus-rollover" src="/assets/site/images/plus-rollover.svg" />
                        </div>
                    </div>

                    <!-- Lazy image -->
                    <div
                        v-else
                        data-aos="custom-fade-in"
                        class="bgimg"
                        v-lazy-bg="item.image?.[0]?.bgStyle"
                    >
                        <div v-if="rollover" class="rollover">
                            <inline-svg class="plus-rollover" src="/assets/site/images/plus-rollover.svg" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="swiper-pagination"></div>
        </div>
    </div>
</template>

<script>
import Swiper, { Navigation, Pagination } from 'swiper';
import 'swiper/swiper-bundle.min.css';
Swiper.use([Navigation, Pagination]);

export default {
    name: 'GridItemCarousel',
    props: {
        rollover: { type: Boolean, required: true },
        json: { type: Array, required: true },
    },
    data() {
        return {
            document: document,
            window: window,
            slider: null,
            swiperContainerId: null,
            swiperContainerSel: null,
            timeoutId: null,
            vimeoPlayer: null,
            currentVimeoId: null,
            carouselObserver: null,
            isCarouselInView: false,
            autoplayImmediately: true,
        };
    },
    mounted() {
        this.swiperContainerId = 'fs-' + Math.floor(Math.random() * 1000000000);
        this.swiperContainerSel = '#' + this.swiperContainerId;
        this.$nextTick(() => {
            this.init();

            if (this.autoplayImmediately) {
                this.resumePlayback();
            } else {
                this.carouselObserver = new IntersectionObserver(
                    entries => {
                        entries.forEach(entry => {
                            this.isCarouselInView = entry.isIntersecting;
                            if (entry.isIntersecting) {
                                this.resumePlayback();
                            } else {
                                this.pausePlayback();
                            }
                        });
                    },
                    { threshold: 0.0 }
                );
                this.carouselObserver.observe(this.$el);
            }

            this.window.addEventListener('resize', this.debounce(() => {
                if (this.slider) this.slider.update();
                if (window.AOS?.refresh) window.AOS.refresh();
                document.body.classList.add('disable-transitions');
                setTimeout(() => document.body.classList.remove('disable-transitions'), 300);
            }, 200));
        });
    },
    directives: {
        lazyBg: {
            mounted(el, binding) {
                if (!binding.value) return;
                el.style.backgroundImage = 'none';
                const observer = new IntersectionObserver((entries, observer) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && !el.classList.contains('lazy-loaded')) {
                            el.style.cssText += binding.value;
                            el.classList.add('lazy-loaded');
                            observer.unobserve(el);
                        }
                    });
                }, { rootMargin: '0px 0px 200px 0px' });
                observer.observe(el);
            }
        },
        lazyVideo: {
            mounted(el) {
                const observer = new IntersectionObserver((entries, observer) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const source = el.querySelector('source[data-src]');
                            if (source) {
                                source.setAttribute('src', source.getAttribute('data-src'));
                                source.removeAttribute('data-src');
                                el.load();
                            }
                            observer.unobserve(el);
                        }
                    });
                }, { threshold: 0.0 });
                observer.observe(el);
            }
        }
    },
    methods: {
        debounce(func, wait = 100) {
            let timeout;
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), wait);
            };
        },
        init() {
            if (this.json.length > 1) {
                this.slider = new Swiper(this.swiperContainerSel, {
                    init: false,
                    speed: 750,
                    grabCursor: true,
                    centeredSlides: true,
                    loop: true,
                    slidesPerView: 1,
                    allowSlidePrev: true,
                    allowSlideNext: true,
                    simulateTouch: true,
                    preventClicks: false,
                    preventClicksPropagation: false,
                    touchStartPreventDefault: false,
                    allowTouchMove: true,
                    navigation: {
                        nextEl: this.swiperContainerSel + ' .swiper-button-next',
                        prevEl: this.swiperContainerSel + ' .swiper-button-prev',
                    },
                    pagination: {
                        el: this.swiperContainerSel + ' .swiper-pagination',
                        type: 'bullets',
                        clickable: true,
                    },
                });
                this.slider.on('slideChangeTransitionEnd', this.onSlideChange);
                this.addSwipeListeners();
                this.$nextTick(() => {
                    this.slider.on('afterInit', this.onInit);
                    setTimeout(() => this.slider.init(), 100);
                });
            } else {
                this.onSlideChange();
            }
        },
        onInit() {
            if (this.json.length <= 1) this.onSlideChange();
        },
        addSwipeListeners() {
            const container = this.document.querySelector(this.swiperContainerSel);
            const overlays = container.querySelectorAll('.swipe-overlay');
            overlays.forEach(overlay => {
                let startX = 0;
                let deltaX = 0;
                overlay.addEventListener('touchstart', e => startX = e.touches[0].clientX);
                overlay.addEventListener('touchmove', e => deltaX = e.touches[0].clientX - startX);
                overlay.addEventListener('touchend', () => {
                    if (Math.abs(deltaX) > 50) {
                        deltaX > 0 ? this.slider.slidePrev() : this.slider.slideNext();
                    }
                    startX = 0;
                    deltaX = 0;
                });
            });
        },
        onSlideChange() {
            clearTimeout(this.timeoutId);
            if (!this.autoplayImmediately && !this.isCarouselInView) return;

            const container = this.document.querySelector(this.swiperContainerSel);
            const currentSlide = container?.querySelector('.swiper-slide.swiper-slide-active');
            if (!currentSlide) return;

            const iframeHolder = currentSlide.querySelector('.iframe-holder');
            if (iframeHolder) {
                const vimeoId = iframeHolder.getAttribute('data-vimeoId');
                if (vimeoId) {
                    if (this.vimeoPlayer && this.currentVimeoId === vimeoId) {
                        this.vimeoPlayer.setVolume(1);
                        return;
                    }
                    if (this.vimeoPlayer) this.vimeoPlayer.unload().then(() => this.vimeoPlayer = null);
                    const iframe = document.createElement('iframe');
                    iframe.src = `https://player.vimeo.com/video/${vimeoId}?api=1&controls=1`;
                    iframe.setAttribute('frameborder', '0');
                    iframe.setAttribute('allow', 'fullscreen');
                    iframe.setAttribute('allowfullscreen', true);
                    iframe.onload = () => {
                        const vp = new this.window.Vimeo.Player(iframe);
                        this.currentVimeoId = vimeoId;
                        vp.setVolume(1).then(() => vp.play()).catch(() => {
                            vp.setVolume(1);
                        });
                        vp.on('ended', () => {
                            this.slider.slideNext();
                            vp.unload();
                        });
                        this.vimeoPlayer = vp;
                    };
                    iframeHolder.innerHTML = '';
                    iframeHolder.appendChild(iframe);
                    return;
                }
            }

            const video = currentSlide.querySelector('video');
            if (video) {
                video.currentTime = 0;
                video.volume = 0;
                video.muted = true;

                video.addEventListener('play', () => {
                    video.muted = true;
                    video.volume = 0;
                });

                video.addEventListener('volumechange', () => {
                    if (!video.muted || video.volume > 0) {
                        video.muted = true;
                        video.volume = 0;
                    }
                });
                video.addEventListener('click', e => {
                    e.preventDefault();
                    video.muted = true;
                    video.volume = 0;
                });
                video.addEventListener('dblclick', e => {
                    e.preventDefault();
                    video.muted = true;
                    video.volume = 0;
                });

                video.play().catch(() => {});

                const checkVideoEnd = () => {
                    video.muted = true;
                    if (video.duration - video.currentTime <= 0.2) {
                        video.removeEventListener('timeupdate', checkVideoEnd);
                        if (this.json.length > 1) this.slider.slideNext();
                    }
                };
                video.addEventListener('timeupdate', checkVideoEnd);
                return;
            }

            if (this.json.length > 1) {
                this.timeoutId = setTimeout(() => this.slider.slideNext(), 4000);
            }
        },
        pausePlayback() {
            this.isCarouselInView = false;
            clearTimeout(this.timeoutId);
            const container = this.document.querySelector(this.swiperContainerSel);
            container?.querySelectorAll('video').forEach(video => video.pause?.());
            this.vimeoPlayer?.pause?.();
        },
        resumePlayback() {
            if (!this.isCarouselInView) this.isCarouselInView = true;
            setTimeout(() => this.onSlideChange(), 50);
        },
    },
    beforeUnmount() {
        clearTimeout(this.timeoutId);
        this.vimeoPlayer?.unload();
        this.carouselObserver?.disconnect();
    }
};
</script>

<style lang="scss">
.swipe-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 60%;
    z-index: 15;
    background: transparent;
    cursor: grab;
    pointer-events: auto;
}
.swiper-wrapper.individual .swipe-overlay {
    display: none;
}
.iframe-holder {
    position: relative;
    z-index: 5;
}
.carousel-item {
    width: 100%;
    height: 100%;
    overflow: hidden;
    backface-visibility: hidden;
    transform: translateZ(0);
}
.bgimg {
    min-height: 100%;
    height: 100%;
    width: 100%;
    backface-visibility: hidden;
    transform: translateZ(0);
}
.swiper-slide {
    will-change: transform;
}
body.disable-transitions *,
body.disable-transitions *::before,
body.disable-transitions *::after {
    transition: none !important;
}
</style>
